import { h, reactive, watch } from "vue";
import { ElNotification, ElMessageBox, ElProgress } from "element-plus";
import { storeOrderItem } from "@/api/user";
import { fetchIllustrateCreateOrder } from "@/api/illustrate";
import { useUploadStorage } from "./useUploadStorage";
import "@/assets/css/storageTask.css";

// 任务持有独立文件与请求数据，生命周期不依赖入库弹窗。
const pendingTasks = new Map();
let leavePrompt = null;

export function installStorageTaskGuards(router) {
  router.beforeEach(async (to, from) => {
    if (!pendingTasks.size || to.fullPath === from.fullPath) return true;
    if (!leavePrompt) {
      leavePrompt = ElMessageBox.alert(
        "图片上传或入库尚未完成，请等待右下角任务结果后再离开。当前页面仍可继续操作。",
        "入库任务进行中",
        { type: "warning", confirmButtonText: "留在当前页面", closeOnClickModal: false },
      ).catch(() => undefined).finally(() => { leavePrompt = null; });
    }
    await leavePrompt;
    return false;
  });
}

function beforeUnload(event) {
  if (!pendingTasks.size) return;
  event.preventDefault();
  event.returnValue = "";
}

export function startStorageTask({ orderIds, orderNos, weight, files, illustrateData, onStored }) {
  const ids = orderIds.map((id) => String(id ?? ""));
  if (!ids.length || ids.some((id) => !id) || !files.length) {
    throw new Error("订单或图片信息不完整，请重新选择");
  }
  if ([...pendingTasks.values()].some((task) => task.ids.some((id) => ids.includes(id)))) {
    throw new Error("所选订单已有入库任务正在处理，请勿重复提交");
  }

  const taskId = Symbol("storage");
  const snapshot = {
    ids: [...ids],
    weight,
    files: [...files],
    illustrateData: illustrateData ? JSON.parse(JSON.stringify(illustrateData)) : null,
  };
  const label = ids.length > 1
    ? `批量入库 · ${ids.length} 个订单`
    : `订单 ${orderNos[0] || ids[0]}`;
  const state = reactive({ stage: "正在上传图片", progress: 0 });
  pendingTasks.set(taskId, snapshot);
  window.addEventListener("beforeunload", beforeUnload);

  const ProgressContent = {
    setup: () => () => h("div", { class: "storage-task-content" }, [
      h("div", { class: "storage-task-order" }, label),
      h("div", { class: "storage-task-stage" }, state.stage),
      h(ElProgress, { percentage: state.progress, strokeWidth: 6 }),
      h("div", { class: "storage-task-hint" }, "后台处理中，可继续操作当前页面"),
    ]),
  };
  const notice = ElNotification({
    title: "入库处理中",
    message: h(ProgressContent),
    type: "info",
    duration: 0,
    showClose: false,
    customClass: "storage-task-notification",
    position: "bottom-right",
  });

  // 每个任务独享上传实例，避免下一次选图或关闭弹窗清空正在上传的文件。
  const run = async () => {
    let stored = false;
    let stage = "upload";
    let result;
    let stopProgress;
    try {
      const uploader = useUploadStorage();
      stopProgress = watch(uploader.uploadProgress, (progress) => { state.progress = progress; });
      uploader.uploadFileList.value = snapshot.files;
      // uploadAllSelected 在任意图片失败时抛错，不允许部分图片成功后继续入库。
      const keys = await uploader.uploadAllSelected();
      state.progress = 100;
      state.stage = "图片已上传，正在提交入库";
      stage = "storage";
      const response = await storeOrderItem({ id: snapshot.ids, urls: keys, weight: snapshot.weight });
      if (response?.code !== 0) throw new Error(response?.message || "入库失败");
      stored = true;
      if (snapshot.illustrateData) {
        state.stage = "已入库，正在创建图鉴";
        stage = "illustrate";
        const response = await fetchIllustrateCreateOrder({
          ...snapshot.illustrateData,
          thumbnail: keys[0] || snapshot.illustrateData.thumbnail,
        });
        if (response?.code !== 0) throw new Error(response?.message || "创建图鉴失败");
      }
      result = { type: "success", title: "入库完成", message: `${label}，已处理完成。` };
    } catch (error) {
      const detail = error?.message || "请求异常";
      result = {
        type: stored ? "warning" : "error",
        title: stored ? "已入库，图鉴创建异常" : "入库任务异常",
        message: stage === "upload"
          ? `${label}：图片上传失败，尚未提交入库。请重新选择图片后提交。原因：${detail}`
          : stored
            ? `${label}：入库已成功，请核对图鉴，勿重复入库。原因：${detail}`
            : `${label}：入库请求异常，请先刷新列表核对订单状态，再决定是否重新提交。原因：${detail}`,
      };
    } finally {
      stopProgress?.();
      pendingTasks.delete(taskId);
      if (!pendingTasks.size) window.removeEventListener("beforeunload", beforeUnload);
      notice.close();
    }
    ElNotification({
      ...result,
      duration: result.type === "success" ? 5000 : 0,
      showClose: true,
      position: "bottom-right",
      customClass: "storage-task-notification",
    });
    // 刷新失败不能把已经成功的入库误报为失败。
    if (stored) {
      try { await onStored?.(); } catch (error) { console.error("入库完成后刷新列表失败", error); }
    }
  };
  void run();
}
